import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService } from '@/services';

/** O usuário já definiu a senha: não há convite a reenviar (a Api responde 409). */
export class FirstAccessAlreadyCompletedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FirstAccessAlreadyCompletedError';
  }
}

/** Toast do reenvio que falhou: aviso neutro quando o primeiro acesso já foi concluído. */
export function getResendFirstAccessErrorToast(error: unknown) {
  if (error instanceof FirstAccessAlreadyCompletedError) {
    return {
      type: 'info' as const,
      title: 'Primeiro acesso já concluído',
      description: error.message,
    };
  }
  return {
    type: 'error' as const,
    title: 'Erro ao reenviar convite',
    description: (error instanceof Error
      ? error.message
      : 'Falha ao reenviar convite'
    ).trim(),
  };
}

export function useResendFirstAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const result = await usersService.resendFirstAccessInvitation(email);
      if (result.alreadyCompleted)
        throw new FirstAccessAlreadyCompletedError(
          result.error ||
            'Este usuário já concluiu o primeiro acesso. Se precisar, use "Redefinir senha".'
        );
      if (!result.success)
        throw new Error(result.error || 'Falha ao reenviar convite');
      return result.message;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['users'] }),
        queryClient.refetchQueries({ queryKey: ['advisories-users'] }),
        queryClient.refetchQueries({ queryKey: ['broker-users'] }),
      ]);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['users'] }),
        queryClient.invalidateQueries({ queryKey: ['advisories-users'] }),
        queryClient.invalidateQueries({ queryKey: ['broker-users'] }),
      ]);
    },
  });
}
